import React, { memo, useEffect, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaTimes,
  FaSpinner,
  FaDownload,
  FaTable
} from 'react-icons/fa';
import { Modal } from '@/shared/components/modals';
import { useEvaluationTables } from '../../EvaluationTables/hooks/useEvaluationTables';
import { DataTable } from '../../EvaluationTables/DataTable';
import { convertToTableData } from '../../EvaluationTables/utils';

interface EvaluationTablesModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
}

/**
 * Modal for displaying evaluation tables (df_eval and df_eval_ts)
 * Shows metrics in scrollable tables with download option
 */
export const EvaluationTablesModal: React.FC<EvaluationTablesModalProps> = memo(({
  isOpen,
  onClose,
  sessionId
}) => {
  const { t } = useTranslation('training');
  const titleId = useId();
  const {
    dfEval,
    dfEvalTs,
    loading,
    error,
    fetchEvaluationTables,
    downloadEvaluationTables,
    downloadEvaluationTablesTs
  } = useEvaluationTables(sessionId);

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen && sessionId) {
      fetchEvaluationTables();
    }
  }, [isOpen, sessionId, fetchEvaluationTables]);

  // Convert dfEval to table format using existing utility
  const tables = useMemo(() => {
    if (!dfEval) return [];
    return convertToTableData(dfEval);
  }, [dfEval]);

  const hasData = tables.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleId={titleId}
      size="4xl"
      maxHeightClass="max-h-[80vh]"
      className="ring-1 ring-border/60 animate-slideUp rounded-xl shadow-2xl"
    >
      {/* Header */}
      <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <h2 id={titleId} className="text-base font-semibold text-foreground tracking-tight">
          {t('training.sidebar.evaluationTitle', 'Evaluation Tables')}
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={downloadEvaluationTables}
            disabled={!dfEval || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-muted text-foreground rounded-lg hover:bg-primary hover:text-primary-foreground disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed transition-all duration-150"
            title={t('training.evaluation.downloadTables', 'Download Tables')}
            aria-label={t('training.evaluation.downloadTables', 'Download Tables')}
          >
            <FaTable className="text-[10px]" />
            <span className="hidden sm:inline">{t('training.evaluation.downloadTables', 'Tabellen')}</span>
          </button>
          <button
            type="button"
            onClick={downloadEvaluationTablesTs}
            disabled={!dfEvalTs || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-muted text-foreground rounded-lg hover:bg-primary hover:text-primary-foreground disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed transition-all duration-150"
            title={t('training.evaluation.downloadTablesTs', 'Download Timestep Data')}
            aria-label={t('training.evaluation.downloadTablesTs', 'Download Timestep Data')}
          >
            <FaDownload className="text-[10px]" />
            <span className="hidden sm:inline">{t('training.evaluation.downloadTablesTs', 'Zeitschritte')}</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 ml-1"
            aria-label={t('training.sidebar.close', 'Close')}
          >
            <FaTimes size={14} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 overflow-y-auto max-h-[60vh] bg-muted/30">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3">
              <FaSpinner className="animate-spin text-muted-foreground" />
            </div>
            <span className="text-sm text-muted-foreground">{t('training.sidebar.loading', 'Loading...')}</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200/80 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <span className="text-red-500 text-xs font-bold">!</span>
              </div>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        ) : !hasData ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
              <FaTable className="text-muted-foreground/50" />
            </div>
            <p className="text-sm text-muted-foreground">{t('training.sidebar.evaluationEmpty', 'No evaluation data available')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {tables.map((table, index) => (
              <DataTable key={index} tableData={table} />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end px-6 py-4 border-t border-border bg-card">
        <button
          type="button"
          onClick={onClose}
          className="text-sm font-medium text-foreground hover:text-foreground bg-muted hover:bg-muted/80 px-5 py-2 rounded-lg transition-all duration-150"
        >
          {t('training.sidebar.close', 'Close')}
        </button>
      </div>
    </Modal>
  );
});

EvaluationTablesModal.displayName = 'EvaluationTablesModal';

export default EvaluationTablesModal;
